<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { worldStore, dispatch } from '@sim/store';
  import { SKILL_META, roomDef, traitById } from '@sim/config';
  import { SKILL_KEYS, NEED_KEYS } from '@sim/types';
  import { moodBand, needColor, activityLabel, roleLabel } from '@sim/selectors';
  import { assignedTo, activeBuilding } from '@sim/world';

  export let id: number;
  const emit = createEventDispatcher();

  $: city = $worldStore;
  $: b = activeBuilding(city)!;
  $: c = b.colonists.find((x) => x.id === id);
  $: workplaces = b.rooms.filter((r) => roomDef(r.type).workplace);
  $: recent = c ? c.recentThoughts.filter((t) => t.expires > city.t) : [];

  function assign(roomId: number | null) {
    if (c) dispatch({ type: 'assign', colonistId: c.id, roomId });
  }
  function full(roomId: number): boolean {
    const room = b.rooms.find((r) => r.id === roomId)!;
    return assignedTo(b, roomId).length >= roomDef(room.type).workSlots && c?.assignedRoomId !== roomId;
  }
</script>

{#if c}
  <div class="sheet">
    <div class="head">
      <div class="who">
        <span class="name">{c.name}</span>
        <span class="role">{roleLabel(c)} · {activityLabel(c)}</span>
      </div>
      <span class="mood" style="color:{moodBand(c.mood).color}">{moodBand(c.mood).label}</span>
      <button class="x" on:click={() => emit('close')}>✕</button>
    </div>

    {#if c.traits.length}
      <div class="traits">
        {#each c.traits as t}
          <span class="trait" title={traitById(t)?.blurb}>{traitById(t)?.name ?? t}</span>
        {/each}
      </div>
    {/if}

    <div class="needs">
      {#each NEED_KEYS as need}
        <div class="need">
          <span class="nl">{need}</span>
          <span class="nbar"><span class="nfill" style="width:{c.needs[need]}%; background:{needColor(c.needs[need])}"></span></span>
        </div>
      {/each}
    </div>

    <div class="skills">
      {#each SKILL_KEYS as k}
        <div class="skill">
          <span>{SKILL_META[k].icon} {SKILL_META[k].name}</span>
          <span class="lvl mono">{c.skills[k].level}</span>
        </div>
      {/each}
    </div>

    {#if recent.length}
      <div class="recent">
        {#each recent as t (t.id)}
          <span class="rt" class:up={t.mood > 0} class:down={t.mood < 0}>
            {t.label} <span class="rtm mono">{t.mood > 0 ? '+' : ''}{t.mood}</span>
          </span>
        {/each}
      </div>
    {/if}

    <div class="assign">
      <div class="atitle">Assign to a workplace</div>
      <div class="aopts">
        <button class="aopt" class:on={c.assignedRoomId == null} on:click={() => assign(null)}>Unassigned</button>
        {#each workplaces as r}
          <button class="aopt" class:on={c.assignedRoomId === r.id} disabled={full(r.id)} on:click={() => assign(r.id)}>
            {roomDef(r.type).icon} {roomDef(r.type).name} <span class="cap mono">{assignedTo(b, r.id).length}/{roomDef(r.type).workSlots}</span>
          </button>
        {/each}
      </div>
    </div>
  </div>
{/if}

<style>
  .sheet {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 12px;
    box-shadow: var(--shadow);
  }
  .head {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .who {
    display: flex;
    flex-direction: column;
  }
  .name {
    font-weight: 800;
    font-size: 15px;
  }
  .role {
    font-size: 11px;
    color: var(--ink-dim);
  }
  .mood {
    margin-left: auto;
    font-size: 12px;
    font-weight: 700;
  }
  .x {
    font-size: 15px;
    color: var(--ink-dim);
    padding: 2px 6px;
  }
  .traits {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin: 8px 0;
  }
  .trait {
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink-dim);
  }
  .needs {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 8px 0;
  }
  .need {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .nl {
    width: 38px;
    font-size: 11px;
    color: var(--ink-dim);
    text-transform: capitalize;
  }
  .nbar {
    flex: 1;
    height: 7px;
    border-radius: 4px;
    background: #000;
    overflow: hidden;
    border: 1px solid var(--line);
  }
  .nfill {
    display: block;
    height: 100%;
    transition: width 0.2s linear;
  }
  .skills {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 12px;
    margin: 8px 0;
  }
  .skill {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: var(--ink-dim);
    padding: 2px 0;
    border-bottom: 1px solid var(--line);
  }
  .lvl {
    color: var(--ink);
    font-weight: 700;
  }
  .recent {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin: 4px 0 8px;
  }
  .rt {
    font-size: 10px;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink-dim);
  }
  .rt.up {
    color: var(--good);
    border-color: color-mix(in srgb, var(--good) 35%, var(--line));
  }
  .rt.down {
    color: var(--bad);
    border-color: color-mix(in srgb, var(--bad) 35%, var(--line));
  }
  .rtm {
    opacity: 0.75;
  }
  .atitle {
    font-size: 11px;
    color: var(--ink-faint);
    margin: 6px 0 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .aopts {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }
  .aopt {
    font-size: 11px;
    padding: 6px 9px;
    border-radius: 8px;
    background: var(--panel-2);
    border: 1px solid var(--line);
    color: var(--ink);
  }
  .aopt.on {
    border-color: var(--good);
    color: var(--good);
  }
  .cap {
    color: var(--ink-faint);
    font-size: 10px;
  }
</style>
